import { IDropdownOption } from "@fluentui/react";

// Country metadata for phone validation (local number length excludes country code)
export const countries = [
  {
    code: "+1",
    flag: "🇺🇸",
    name: "United States",
    digits: 10,
    groups: [3, 3, 4],
  },
  { code: "+1", flag: "🇨🇦", name: "Canada", digits: 10, groups: [3, 3, 4] },
  {
    code: "+381",
    flag: "🇷🇸",
    name: "Serbia",
    minDigits: 8,
    maxDigits: 9,
    groups: [3, 3, 3],
  },
  { code: "+61", flag: "🇦🇺", name: "Australia", digits: 9, groups: [3, 3, 3] },
];

export const formatLocalByGroups = (localDigits: string, groups?: number[]) => {
  const d = (localDigits || "").replace(/\D+/g, "");
  if (!d) return "";
  if (groups && groups.length) {
    const parts: string[] = [];
    let i = 0;
    for (const g of groups) {
      if (i >= d.length) break;
      parts.push(d.slice(i, Math.min(i + g, d.length)));
      i += g;
    }
    if (i < d.length) parts.push(d.slice(i));
    return parts.join(" ");
  }
  // Fallback: chunk by 3
  const parts: string[] = [];
  for (let i = 0; i < d.length; i += 3) {
    parts.push(d.slice(i, Math.min(i + 3, d.length)));
  }
  return parts.join(" ");
};

export const extractKeyCandidate = (v: any): any => {
  if (v === null || v === undefined || v === "") return undefined;
  if (Array.isArray(v)) return v.map(extractKeyCandidate);
  if (typeof v === "object")
    return (
      v.Id ?? v.ID ?? v.key ?? v.value ?? v.Title ?? v.Name ?? v.name ?? v.text
    );
  return v;
};

export const coerceToOptionKeyType = (
  candidate: any,
  options: IDropdownOption[],
) => {
  if (candidate === undefined) return undefined;
  if (!options?.length) return candidate;
  const keyType = typeof options[0].key;
  if (keyType === "number") {
    const n = Number(candidate);
    return Number.isNaN(n) ? undefined : n;
  }
  return String(candidate);
};

export const normalizeSelectedKey = (
  value: any,
  options: IDropdownOption[],
): IDropdownOption["key"] | undefined => {
  const cand = extractKeyCandidate(value);
  const coerced = coerceToOptionKeyType(cand, options);
  if (options.some((o) => o.key === coerced)) return coerced;

  const candStr =
    cand === undefined || cand === null ? undefined : String(cand).trim();
  if (!candStr) return undefined;

  const byText = options.find(
    (o) =>
      String(o.text ?? "").trim() === candStr ||
      String(o?.data?.Title ?? "").trim() === candStr ||
      String(o?.data?.Name ?? "").trim() === candStr ||
      String(o?.data?.name ?? "").trim() === candStr,
  );
  return byText?.key;
};

export const normalizeSelectedKeys = (
  value: any,
  options: IDropdownOption[],
): Array<IDropdownOption["key"]> => {
  const arr = Array.isArray(value) ? value : [];
  const keys = new Set<IDropdownOption["key"]>();

  arr.forEach((v) => {
    const cand = extractKeyCandidate(v);
    const coerced = coerceToOptionKeyType(cand, options);
    const byKey = options.find((o) => o.key === coerced);
    if (byKey) {
      keys.add(byKey.key);
      return;
    }
    const candStr =
      cand === undefined || cand === null ? undefined : String(cand).trim();
    if (!candStr) return;
    const byText = options.find(
      (o) =>
        String(o.text ?? "").trim() === candStr ||
        String(o?.data?.Title ?? "").trim() === candStr ||
        String(o?.data?.Name ?? "").trim() === candStr ||
        String(o?.data?.name ?? "").trim() === candStr,
    );
    if (byText) keys.add(byText.key);
  });

  return Array.from(keys);
};

export const getByPath = (obj: any, path: string) =>
  (path || "")
    .split(".")
    .reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
      obj,
    );

export const buildOptionText = (
  item: any,
  dropdownText?: string | string[],
) => {
  const key = item?.ID ?? item?.Id;
  const fallback =
    item?.Title ??
    item?.text ??
    item?.Name ??
    item?.name ??
    item?.value ??
    (typeof item === "string" || typeof item === "number"
      ? String(item)
      : String(key));
  if (!dropdownText) {
    return String(fallback ?? "");
  }
  const fields = Array.isArray(dropdownText)
    ? dropdownText
    : dropdownText
        .split(/[|,]/)
        .map((s) => s.trim())
        .filter(Boolean);
  const parts = fields
    .map((f) => getByPath(item, f))
    .filter((v) => v !== undefined && v !== null && String(v).trim().length > 0)
    .map((v) => String(v));
  const text = parts.join(" - ");
  return text && text.trim().length > 0 ? text : String(fallback ?? "");
};

export const toOptionsForField = (
  dropdownText: string | string[] | undefined,
  arr: any[],
) => {
  const options = (arr || []).map((it: any) => {
    const text = buildOptionText(it, dropdownText);
    const rawKey = it.id ?? text;
    const key = typeof rawKey === "number" ? rawKey : String(rawKey);
    return { key, text, data: it } as IDropdownOption;
  });

  // Separate empty/None values from regular options
  const emptyOptions = options.filter(
    (opt) =>
      !opt.text ||
      opt.text.trim() === "" ||
      opt.text.toLowerCase() === "none" ||
      opt.text.toLowerCase() === "n/a",
  );
  const regularOptions = options.filter(
    (opt) =>
      opt.text &&
      opt.text.trim() !== "" &&
      opt.text.toLowerCase() !== "none" &&
      opt.text.toLowerCase() !== "n/a",
  );

  // Sort regular options alphabetically (case-sensitive: A-Z then a-z)
  regularOptions.sort((a, b) => a.text.localeCompare(b.text));

  // Return empty options first, then sorted regular options
  return [...emptyOptions, ...regularOptions];
};

export const toBool = (v: any): boolean => {
  if (typeof v === "boolean") return v;
  if (v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "1" || s === "yes" || s === "on";
};

// Extract all unique login names from leadership fields (with domain prefix)
export const getAllLeadershipUsernames = (
  fields: (string | undefined)[],
): string[] => {
  const usernames = new Set<string>();

  fields.forEach((field) => {
    if (!field) return;
    const parsed = field
      .split(",")
      .map((item: string) => item.replace(/[[\]]/g, ""))
      .filter(Boolean);

    parsed.forEach((username: string) => {
      // Convert to proper format: POSITIVE\username
      const uppercased = username
        .replace(/positive\\/i, "POSITIVE\\")
        .toUpperCase();
      usernames.add(uppercased);
    });
  });

  return Array.from(usernames);
};

// Get display name (full name if available, otherwise username)
export const getDisplayName = (
  username: string,
  fullNamesMap: Record<string, string>,
): string => {
  const uppercased = username
    .replace(/positive\\/i, "POSITIVE\\")
    .toUpperCase();
  return fullNamesMap[uppercased] || username;
};

// Parse a field string and return comma-separated display names
export const getFieldDisplayNames = (
  fieldString: string,
  fullNamesMap: Record<string, string>,
): string => {
  if (!fieldString) return "";
  const parsed = fieldString
    .split(",")
    .map((item: string) => item.replace(/[[\]]/g, ""))
    .filter(Boolean);
  return parsed
    .map((username) => getDisplayName(username, fullNamesMap))
    .join(", ");
};
